import { Parameters } from '@app/models/Parameters';
import { post, get } from './http.api';
import { ArrivalRequest, ImportRequest } from '@app/models/import/ImportRequest';
import {
  ArrivalResponse,
  ImportApiData,
  ImportResponse,
  UploadDocumentResponse,
  CustomerResponse,
  GetCustomerImportsResponse,
} from '@app/models/import/ImportResponse';
import { ImportTableData, ImportTableRow } from '@app/models/import/ImportResponse';
import { PCComment } from '@app/models/import/ImportRequest';
import { DropdownOption } from '@app/models/DropdownOption';

//const importType = localStorage.getItem('selected_import_type') || 'NORMAL';

export const getImportTableData = async (parameters: Parameters): Promise<ImportTableData> => {
  try {
    const { data } = await get<any>('imports', { params: parameters }, true);

    const page = data?.Imports;

    const imports: ImportTableRow[] =
      page?.data?.map((importData: any) => ({
        id: importData?.id,
        code: importData?.code,
        type: importData?.type,
        customer_id: importData?.customer_id,
        status: importData?.status,
        status_label: importData?.status_label,
        expire_date: importData?.expire_date,
        product_reg_type: importData?.product_reg_type,
        imported_weightage: importData?.imported_weightage != null ? Number(importData.imported_weightage) : null,
      })) ?? [];

    const rawStatus = data?.status_options ?? {};
    const statusOptions: DropdownOption[] = Array.isArray(rawStatus)
      ? rawStatus
      : Object.entries(rawStatus).map(([value, label]) => ({ value, label: String(label) }));

    return {
      data: imports,
      pagination: {
        current: page?.current_page || 1,
        pageSize: page?.per_page || 10,
        total: page?.total || 0,
      },
      status_options: statusOptions,
    };
  } catch (error: any) {
    console.error('Error fetching Imports:', error);
    throw error;
  }
};

export const getAllImports = async (): Promise<ImportResponse> => {
  try {
    const { data, status } = await get<ImportResponse>('imports', undefined, true);
    console.log(`Status: ${status}`, `Data: ${JSON.stringify(data)}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw error;
  }
};

export const getImportById = async (id: number): Promise<ImportResponse> => {
  try {
    const { data, status } = await get<ImportResponse>(`imports/${id}`, undefined, true);
    console.log(`Status: ${status}`, `Data: ${JSON.stringify(data)}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw error;
  }
};

export const storeImport = async (importData: ImportRequest) => {
  try {
    const response = await post<any>('imports/create', importData, undefined, true);
    console.log(`Status: ${response.status}`, `Data: ${JSON.stringify(response.data)}`);
    return response.data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw error;
  }
};

export const createImport = async (): Promise<ImportApiData> => {
  try {
    const importType = localStorage.getItem('selected_import_type') || 'NORMAL';
    const { data, status } = await get<ImportApiData>(
      'imports/create',
      {
        params: { type: importType },
      },
      true,
    );

    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const showImport = async (id: number) => {
  try {
    const { data, status } = await get<ImportApiData>(`imports/${id}`, undefined, true);
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const editImport = async (id: number) => {
  try {
    const { data, status } = await get<ImportApiData>(`imports/${id}/edit`, undefined, true);
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const updateImport = async (id: number, updateImportData: ImportRequest) => {
  try {
    const { data, status } = await post<ImportRequest>(`imports/${id}/edit`, updateImportData, undefined, true);
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const updateStatus = async (id: number, action: any) => {
  try {
    const { data, status } = await post<ImportRequest>(`imports/${id}/do_action`, action, undefined, true);
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const storeComment = async (id: number, commentData: PCComment) => {
  try {
    const response = await post<any>(`imports/${id}/store_comment`, commentData, undefined, true);
    console.log(`Status: ${response.status}`, `Data: ${JSON.stringify(response.data)}`);
    return response.data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw error;
  }
};

export const updateComment = async (id: number, com_id: number, commentData: PCComment) => {
  try {
    const { data, status } = await post<PCComment>(
      `imports/${id}/${com_id}/update_comment`,
      commentData,
      undefined,
      true,
    );
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const uploadDocument = async (import_id: number, formData: FormData): Promise<UploadDocumentResponse | null> => {
  try {
    const { data, status } = await post<UploadDocumentResponse>(`imports/${import_id}/upload_document`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    console.log('Status:', status, 'Data:', data);
    return data;
  } catch (error: any) {
    console.error('Upload error:', error);

    if (error.response) {
      const { status, data } = error.response;
    } else {
      alert('A network error occurred. Please check your connection.');
    }

    return null;
  }
};

export const loadVarieties = async (importId: number, productId: number) => {
  try {
    console.log('Making request with product_id:', productId);
    const { data, status } = await get<{ product_variety_options: any }>(
      `/imports/${importId}/load_varieties?product_id=${productId}`,
    );
    console.log(`Status: ${status}, Data:`, data);
    return data.product_variety_options;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const storeArrival = async (importId: number, request: ArrivalRequest) => {
  try {
    const { data, status } = await post<ArrivalResponse>(`imports/${importId}/store_arrival`, request);
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const updateArrival = async (importId: number, arrivalId: number, request: ArrivalRequest) => {
  try {
    const { data, status } = await post<ArrivalResponse>(`imports/${importId}/${arrivalId}/update_arrival`, request);
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export interface UploadAdditionalDocumentResponse {
  data: {
    import_additional_documents: {
      name: string;
      file: string;
      document_id: string;
    }[];
    import_documents: any[];
  };
  message: string;
}

export const uploadAdditionalDocument = async (
  import_id: number,
  formData: FormData,
): Promise<UploadAdditionalDocumentResponse | null> => {
  try {
    const { data, status } = await post<UploadAdditionalDocumentResponse>(
      `imports/${import_id}/upload_additional`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );

    console.log('Status:', status, 'Data:', data);
    return data;
  } catch (error: any) {
    console.error('Upload error:', error);

    if (error.response) {
      const { status, data } = error.response;
      console.error(`Error ${status}:`, data.errors || data.message);
    } else {
      alert('A network error occurred. Please check your connection.');
    }

    return null;
  }
};

export const findCustomerByEmail = async (
  email: string,
  prOptions: any[],
  pcOptions: any[],
): Promise<CustomerResponse> => {
  try {
    const { data, status } = await get<CustomerResponse>(
      `imports/find_customer?email=${encodeURIComponent(email)}`,
      {},
      true,
    );

    console.log(`Status: ${status}`, `Data:`, data);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const getCustomerImports = async (): Promise<GetCustomerImportsResponse> => {
  try {
    const { data, status } = await get<GetCustomerImportsResponse>('/imports/customer-list', undefined, true);
    console.log(`Status: ${status}`, 'Data:', data);
    return data;
  } catch (error: any) {
    console.error('Error fetching customer imports:', error);
    throw error;
  }
};

export const generateInvoice = async (id: number, invoiceType: string) => {
  try {
    const response = await post<any>(`imports/${id}/create_invoice`, { invoice_type: invoiceType }, undefined, true);
    console.log(`Status: ${response.status}`, `Data: ${JSON.stringify(response.data)}`);
    return response.data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw error;
  }
};

export const getInvoice = async (id: number) => {
  try {
    const { data, status } = await get<any>(`/invoices/${id}`, undefined, true);
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};
export const updateAdditionalInfo = async (importId: number, request: any) => {
  try {
    const { data, status } = await post<{ id: number }>(`imports/${importId}/update_additinal_info`, request);
    console.log(`Status: ${status}`, `Data:`, data);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const getProductOptions = async (import_id: number, arrival_id: number) => {
  try {
    const { data, status } = await get<any>(
      `imports/${import_id}/arrivals/${arrival_id}/get_product_options`,
      undefined,
      true,
    );
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const getVarietyOptions = async (import_id: number, arrival_id: number, arrival_line: number) => {
  try {
    const { data, status } = await get<any>(
      `imports/${import_id}/arrivals/${arrival_id}/arrival_lines/${arrival_line}/get_variety_options`,
      undefined,
      true,
    );
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};
